import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-triggered animations
 * @param {Object} options - IntersectionObserver options
 * @param {boolean} options.once - If true, animation only triggers once (default: true)
 * @param {number} options.threshold - Visibility threshold (default: 0.1)
 * @param {string} options.rootMargin - Root margin (default: "0px 0px -50px 0px")
 */
export function useScrollAnimation(options = {}) {
  const { 
    once = true, 
    threshold = 0.1, 
    rootMargin = "0px 0px -50px 0px" 
  } = options;
  
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) {
            observer.unobserve(element);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [once, threshold, rootMargin]);

  return [ref, isVisible];
}

/**
 * Animated wrapper component for scroll animations
 */
export function AnimateOnScroll({ 
  children, 
  animation = "fade-up", 
  delay = 0,
  duration = 600,
  once = true,
  className = "",
  ...props 
}) {
  const [ref, isVisible] = useScrollAnimation({ once });

  const animationStyles = {
    'fade-up': {
      initial: { opacity: 0, transform: 'translateY(30px)' },
      animate: { opacity: 1, transform: 'translateY(0)' }
    },
    'fade-down': {
      initial: { opacity: 0, transform: 'translateY(-30px)' },
      animate: { opacity: 1, transform: 'translateY(0)' }
    },
    'fade-left': {
      initial: { opacity: 0, transform: 'translateX(30px)' },
      animate: { opacity: 1, transform: 'translateX(0)' }
    },
    'fade-right': {
      initial: { opacity: 0, transform: 'translateX(-30px)' },
      animate: { opacity: 1, transform: 'translateX(0)' }
    },
    'fade': {
      initial: { opacity: 0 },
      animate: { opacity: 1 }
    },
    'scale': {
      initial: { opacity: 0, transform: 'scale(0.95)' },
      animate: { opacity: 1, transform: 'scale(1)' }
    },
    'scale-up': {
      initial: { opacity: 0, transform: 'scale(0.9) translateY(20px)' },
      animate: { opacity: 1, transform: 'scale(1) translateY(0)' }
    }
  };

  const currentAnimation = animationStyles[animation] || animationStyles['fade-up'];
  const style = isVisible ? currentAnimation.animate : currentAnimation.initial;

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`,
        willChange: 'opacity, transform'
      }}
      {...props}
    >
      {children}
    </div>
  );
}

export default useScrollAnimation;
